import { Alert, Button, Card } from '@pmi/dsm-react';
import { CircleRemoveIcon } from '@pmi/dsm-react/dist/components/icons/16';
import { useRef, useState, BaseSyntheticEvent, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Row, Col, Spinner } from 'react-bootstrap';
import { AlertErrorSolidIcon } from '@pmi/dsm-react/dist/components/icons';
import styles from './logoUploadCard.module.scss';
import LogoRemoveModal from './LogoRemoveModal';
import usePermissions from '../../../shared/hooks/usePermissions';
import PERMISSIONS from '../../../shared/permissions';
import { accountSelector } from '../../../shared/store/shared/accountSlice';
import { useGetAccountDataQuery } from '../../../shared/navigation/apiSlice';
import { useUploadLogoMutation } from './apiSlice';
import { reportError, toastWithAnalytics } from '../../../shared/analytics';

const MAX_FILE_SIZE = 1000000; // 1MB;
const ALLOWED_FILE_EXTENSIONS = [
  'image/jpg',
  'image/jpeg',
  'image/gif',
  'image/png',
];

const intialLogoState = {
  url: 'https://placehold.co/200x200?text=Your+Logo',
};

export default function LogoUploadCard() {
  const { currentAccountId } = useSelector(accountSelector);
  const { hasAccess } = usePermissions();
  const {
    data: accountData,
    isLoading,
    isFetching,
    currentData,
  } = useGetAccountDataQuery(currentAccountId);
  const fileUploadInputRef = useRef(null);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [uploadLogo, { isLoading: isUploading }] = useUploadLogoMutation();
  const [logo, setLogo] = useState(intialLogoState);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (accountData?.logoUrl) {
      setLogo({
        url: accountData.logoUrl,
      });
    } else {
      setLogo(intialLogoState);
    }
  }, [currentData]);

  const handleLogoUpdate = (e: BaseSyntheticEvent) => {
    setError(false);
    const fileReader = new FileReader();
    const file = e.target.files[0] as File;
    fileReader.onload = async () => {
      if (file.size > MAX_FILE_SIZE) {
        toastWithAnalytics(
          'Sorry! Your file exceeds the maximum file size of 1MB',
          'danger',
        );
      } else if (!ALLOWED_FILE_EXTENSIONS.includes(file.type)) {
        toastWithAnalytics(
          `${file.type.toString()} is not allowed. The supported formats are: .jpg, .jpeg, .png, .gif`,
          'danger',
        );
      } else {
        try {
          const formData = new FormData();
          formData.append('logoFile', file);
          await uploadLogo({
            accountId: currentAccountId,
            payload: formData,
          }).unwrap();
          toastWithAnalytics('Your logo was uploaded successfully', 'success');
          e.target.value = '';
        } catch {
          // reset the input field, in case user wants to try again with the same file
          e.target.value = '';
          setError(true);
          reportError('Error updating logo');
        }
      }
    };

    if (file) {
      fileReader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setShowRemoveModal(true);
  };

  const handleModalClose = () => {
    setShowRemoveModal(false);
  };

  const handleModalSuccess = () => {
    setShowRemoveModal(false);
  };

  return (
    <>
      <Card className="p-0">
        <div className="p-4">
          <h4 className="fw-bold">Company Logo</h4>
          <Row className="my-4">
            <Col className="me-4 mb-4" style={{ maxWidth: 200 }}>
              <div className={`${styles['logo-image-container']} m-auto`}>
                {isLoading || isFetching || isUploading ? (
                  <div className="bg-secondary w-100 h-100 d-flex justify-content-center align-items-center">
                    <Spinner className="text-white fs-2" />
                  </div>
                ) : (
                  <img
                    className={`img-fluid w-100 ${styles['logo-image']} object-fit-contain`}
                    src={logo.url}
                    alt="Your company logo"
                  />
                )}
              </div>
            </Col>
            {hasAccess({
              permissions: [PERMISSIONS.UploadAccountLogo],
            }) && (
              <Col>
                <p>
                  Upload your logo and it will be displayed to the users you
                  invite to your company partner platform.
                </p>
                <p className="small">
                  Supported formats: .jpg, .jpeg, .png, .gif | Maximum File
                  Size: 1MB
                </p>
                {error && (
                  <Alert
                    show
                    variant="danger"
                    alertText="We encountered an error updating your logo. Please try again."
                    dismissButtonLabel="Dismiss"
                    icon={AlertErrorSolidIcon}
                    onDismissButtonClick={() => setError(false)}
                  />
                )}
                <input
                  type="file"
                  name="account-logo"
                  className="d-none"
                  accept={ALLOWED_FILE_EXTENSIONS.join(',')}
                  ref={fileUploadInputRef}
                  onChange={handleLogoUpdate}
                  data-testid="file-input"
                />
                <Button
                  titleText={
                    accountData?.logoUrl ? 'Upload New Logo' : 'Upload'
                  }
                  className="me-2 mb-2"
                  size="md"
                  onClick={() => fileUploadInputRef.current.click()}
                />
                {accountData?.logoUrl && (
                  <Button
                    size="md"
                    className="mb-2"
                    titleText="Remove"
                    icon={CircleRemoveIcon}
                    variant="light"
                    onClick={handleRemoveLogo}
                  />
                )}
              </Col>
            )}
          </Row>
        </div>
      </Card>
      {showRemoveModal && (
        <LogoRemoveModal
          onCancel={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </>
  );
}
